import React, { useContext, useEffect, useState } from "react"
import { toast } from "react-toastify"
import api from "../../services/api"
import toastError from "../../errors/toastError"
import { Box, IconButton, Dialog } from "@material-ui/core"
import { useHistory } from "react-router-dom"
import { AuthContext } from "../../context/Auth/AuthContext"
import MessagesList from "../MessagesList"
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext"
import TicketHeader from "../TicketHeader"
import TicketInfo from "../TicketInfo"
import { SocketContext } from "../../context/Socket/SocketContext"
import CloseIcon from "@material-ui/icons/Close"
import useStyles from "./TicketMessagesDialogStyles"
import { EditMessageProvider } from "../../context/EditMessage/EditMessageContext"
import { canais } from "../../utils/canais"
import { usuarioPodeAcessarConversaExterna } from "../../helpers/usuarioPodeAcessarConversaExterna"

export default function TicketMessagesDialog({ open, handleClose, ticketId, ticketInicial }) {
  const history = useHistory()
  const classes = useStyles()

  const { user } = useContext(AuthContext)

  const [, setDrawerOpen] = useState(false)
  const [loading, setLoading] = useState(true)
  const [contact, setContact] = useState({})
  const [ticket, setTicket] = useState({})

  const socketManager = useContext(SocketContext)

  const obterContatoDoTicket = (oTicket) => {
    if (!oTicket) return {}
    return oTicket.contact || oTicket.usuario || oTicket.responsavel || {}
  }

  useEffect(() => {
    let lCancelado = false
    if (open) {
      const lTemTicketInicial = ticketInicial?.id === ticketId

      if (lTemTicketInicial) {
        setContact(obterContatoDoTicket(ticketInicial))
        setTicket(ticketInicial)
        setLoading(false)
      } else {
        setContact({})
        setTicket({})
        setLoading(true)
      }

      const fetchTicket = async () => {
        try {
          const { data } = await api.get("/tickets/" + ticketId)
          if (lCancelado) return

          const { queueId, canalTipoId, responsavelId, usuarioId } = data
          const { profile, id: nUsuarioLogadoId } = user

          // Para conversas internas, verifica se o usuário é participante
          const lEhConversaInterna = canalTipoId === canais.interno
          const lUsuarioEhParticipante =
            lEhConversaInterna && (responsavelId === nUsuarioLogadoId || usuarioId === nUsuarioLogadoId)

          // Para conversas externas, verifica acesso (fila ou responsável sem fila)
          if (!lEhConversaInterna) {
            if (!usuarioPodeAcessarConversaExterna(user, { queueId, responsavelId })) {
              toast.error("Acesso não permitido")
              history.push("/conversas")
              return
            }
          } else {
            // Para conversas internas, verifica se é participante ou admin
            if (!lUsuarioEhParticipante && profile !== "admin") {
              toast.error("Acesso não permitido - você não é participante desta conversa")
              history.push("/conversas")
              return
            }
          }

          setContact(obterContatoDoTicket(data))
          setTicket(data)
          setLoading(false)
        } catch (err) {
          if (!lCancelado) {
            setLoading(false)
            toastError(err)
          }
        }
      }
      fetchTicket()
    }
    return () => {
      lCancelado = true
    }
  }, [ticketId, user, history, open, ticketInicial])

  useEffect(() => {
    const companyId = localStorage.getItem("companyId")
    if (!open) return

    const socket = socketManager.getSocket(companyId)

    const handleTicketEvent = async (data) => {
      if (data.action === "update" && data.ticket.id === ticket.id) {
        setTicket(data.ticket)
      }
      // Evento 'delete' é usado também para remoção de listagens (ex.: transferência / assumir sem responsável)
      if (data.action === "delete" && data.ticketId === ticket.id) {
        toast.warn("Esta conversa foi atualizada.")
        handleClose()
      }
    }

    const handleContactEvent = (data) => {
      if (data.action === "update") {
        setContact((prevState) => {
          if (prevState.id === data.contact?.id) {
            return { ...prevState, ...data.contact }
          }
          return prevState
        })
      }
    }

    socket.on("ready", () => socket.emit("joinChatBox", `${ticket.id}`))
    socket.on(`company-${companyId}-ticket`, handleTicketEvent)
    socket.on(`company-${companyId}-contact`, handleContactEvent)

    return () => {
      socket.off(`company-${companyId}-ticket`, handleTicketEvent)
      socket.off(`company-${companyId}-contact`, handleContactEvent)
    }
  }, [ticketId, ticket, history, open, socketManager])

  const handleDrawerOpen = () => {
    setDrawerOpen(true)
  }

  const renderTicketInfo = () => {
    if (ticket.usuarioId !== undefined) {
      return (
        <div className={classes.ticketInfoWrapper}>
          <TicketInfo contact={contact} ticket={ticket} onClick={handleDrawerOpen} ehEspiar={true} />
          <IconButton aria-label="close" onClick={handleClose} className={classes.closeButton}>
            <CloseIcon />
          </IconButton>
        </div>
      )
    }
  }

  const renderMessagesList = () => {
    if (!ticket?.id) return null

    return (
      <Box className={classes.root}>
        <MessagesList ticket={ticket} ticketId={ticket.id} isGroup={ticket.isGroup} espiando={true}></MessagesList>
      </Box>
    )
  }

  return (
    <Dialog maxWidth="md" onClose={handleClose} open={open} PaperProps={{ className: classes.dialogPaper }}>
      <div className={classes.dialogHeader} data-testid="dialog-header">
        <TicketHeader loading={loading}>{renderTicketInfo()}</TicketHeader>
      </div>
      <EditMessageProvider>
        <ReplyMessageProvider>{renderMessagesList()}</ReplyMessageProvider>
      </EditMessageProvider>
    </Dialog>
  )
}
